// MODIFIED: Fixed missing pagination state and Table props
import React, { useState } from "react";
import {
  CreateTableNew as CreateTable,
  Button,
  SearchBar,
  GeneralMessage,
  // ConfirmationModal
} from "../../../../common/APICommon";
import { StatusBadge } from "../../../../common/Badge";
import { DisapproveReasonModal, ConfirmationModal } from "../../../../common/Modal";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownAltIcon from "@mui/icons-material/ThumbDownAlt";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance"; // Set Loan Detail
import ClassIcon from '@mui/icons-material/Class';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';

import { useSalaryAdvanceApprovals } from "../../../hooks/useSalaryAdvanceApprovals";
import { FinancialRequestDetailsModal } from "./components/FinancialRequestDetailsModal";
import { LoadingSpinner } from "../../../../common/Loading";
import { StrategyTypes, WorkflowStatus } from "../../../../utils/enums";
import { EditApprovedAmountModal } from "./components/EditApprovedAmountModal";
import { EditSalaryAdvanceTypeModal } from "./components/EditSalaryAdvanceTypeModal";
import { useSalaryAdvanceManagement } from "../../../hooks/useSalaryAdvanceManagement";
import { ViewEmployeeSARHistoryModal } from "./components/ViewEmployeeSARHistoryModal";

export function FinancialRequest({ user, userAccess }) {
  const {
    loading,
    data,
    searchTerm,
    setSearchTerm,
    handleAction,
    updateApprovedAmount,
    updateSalaryAdvanceType,
    alertProps
  } = useSalaryAdvanceApprovals(user);

  const { loading : sarLoading, employeeSARHistory, fetchEmployeeLoanHistory } = useSalaryAdvanceManagement(user, userAccess);

  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showFinancialRequestDetailsModal, setShowFinancialRequestDetailsModal] = useState(false);
  const [showEmployeeSARHistoryModal, setShowEmployeeSARHistoryDetailsModal] = useState(false);
  const [showConfirmApproved, setShowConfirmApproved] = useState(false);
  const [showDisapprove, setShowDisapprove] = useState(false);
  const [disapproveTarget, setDisapproveTarget] = useState(null);

  const [confirmationTitle, setConfirmationTitle] = useState("");
  const [showEditApprovedAmountModal, setShowEditApprovedAmountModal] = useState(false);
  const [showEditSalaryAdvanceTypeModal, setShowEditSalaryAdvanceTypeModal] = useState(false);
  const [showConfirmEditApprovedAmount, setShowConfirmEditApprovedAmount] = useState(false);

  const isAmountBase = selectedRequest?.approvalStrategy === StrategyTypes.AMOUNT_BASED;
  // ADDED: Pagination state
  const [currentPage, setCurrentPage] = useState(1);

  function handleCloseConfirm() {
    setConfirmationTitle("");
    setShowConfirmApproved(false);
  }

  async function handleClickEmployeeHistory(data) {
    
    try {
      console.log({data})
      const employeeSARHistory = await fetchEmployeeLoanHistory(data?.employeePersonalInformationId);
    } catch (error) {
      console.error("Error:", error);
    }

    setSelectedRequest(data);
    setShowEmployeeSARHistoryDetailsModal(true);
  }

  const actions = [
    {
      name: "Approve",
      icon: <ThumbUpIcon fontSize="small" />,
      className: "bg-blue-600 hover:bg-blue-700",
      function: (item) => {
        if (item?.approvalStrategy && item.approvalStrategy === "AmountBased") setConfirmationTitle("Confirm Approval");
        else if (item.currentHierarchy && item.currentHierarchy === 1) setConfirmationTitle("Recommended");
        else if (item.currentHierarchy && item.currentHierarchy === 2) setConfirmationTitle("Noted");

        setSelectedRequest(item);
        setShowConfirmApproved(true);
      },
      visible: (item) => item.isPendingMyAction === true,
    },
    {
      name: "Disapprove",
      icon: <ThumbDownAltIcon fontSize="small" />,
      className: "bg-red-500 hover:bg-red-600",
      function: (item) => {
        setDisapproveTarget(item.id);
        setShowDisapprove(true);
      },
      visible: (item) => item.isPendingMyAction === true,
    },
    {
      name: "Edit Loan Amount",
      icon: <AccountBalanceIcon fontSize="small" />,
      className: "bg-blue-500 hover:bg-blue-600",
      function: (item) => {
        setSelectedRequest(item);
        setShowEditApprovedAmountModal(true);
      },
      visible: (item) =>
        item.isPendingMyAction === true &&
        // 2. Strategy must be Amount-Based
        item.approvalStrategy === StrategyTypes.AMOUNT_BASED &&
        // 3. Hide if already Approved, Disapproved, or Cancelled
        item.status !== WorkflowStatus.APPROVED &&
        item.status !== WorkflowStatus.DISAPPROVED &&
        item.status !== WorkflowStatus.CANCELLED &&
        // 4. Hide if a loan has already been created (using the new backend property)
        !item.hasLoan
    },
    {
      name: "Edit Salary Advance Type",
      icon: <ClassIcon fontSize="small" />,
      className: "bg-blue-500 hover:bg-blue-600",
      function: (item) => {
        setSelectedRequest(item);
        setShowEditSalaryAdvanceTypeModal(true);
      },
      visible: (item) =>
        // 1. It must be MY turn to act right now
        item.isPendingMyAction === true &&
        // 2. The current stage must be Level 2
        item.currentHierarchy === 2 &&
        // 3. Status must not be finalized
        item.status !== WorkflowStatus.APPROVED &&
        item.status !== WorkflowStatus.DISAPPROVED &&
        item.status !== WorkflowStatus.CANCELLED &&
        // 4. No loan should exist yet
        !item.hasLoan
    },
    {
      name: "View",
      icon: <VisibilityIcon fontSize="small" />,
      className: "bg-cyan-500 hover:bg-cyan-600",
      function: (item) => {
        setSelectedRequest(item);
        setShowFinancialRequestDetailsModal(true);
      },
      visible: true,
    },
    {
      name: "View Balance",
      icon: <AccountBalanceWalletIcon fontSize="small" />,
      className: "bg-blue-500 hover:bg-blue-600",
      function: (item) => {
        handleClickEmployeeHistory(item);
      },
      visible: true,
    },
  ];

  return (
    <div className="v2-module-container p-4">
      <SearchBar handleFilter={setSearchTerm} value={searchTerm} />

      <CreateTable
        headers={["Ref No.", "Employee", "Vessel", "Amount", "Strategy", "Status", "Actions"]}
        accessors={["referenceNumber", "employeeFullName", "vesselName", "amount", "approvalStrategy", "status", "actions"]}
        data={data}
        actions={actions}
        // MODIFIED: Re-added pagination props
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        itemsPerPage={10}
        totalItems={data?.length || 0}
        customCellRenderer={(item, acc) => {
          if (acc === "amount") return <b>₱{item.amount?.toLocaleString()}</b>;
          if (acc === "status") return <StatusBadge status={item.status} />;
          if (acc === "approvalStrategy") return (
            <span className="text-xs font-bold uppercase text-blue-600">
              {item.approvalStrategy} (LVL {item.currentHierarchy})
            </span>
          );
          return null;
        }}
      />

      {showFinancialRequestDetailsModal && (
        <FinancialRequestDetailsModal
          isOpen={showFinancialRequestDetailsModal}
          onClose={() => setShowFinancialRequestDetailsModal(false)}
          request={selectedRequest}
        />
      )}

      {showEditApprovedAmountModal && (
        <EditApprovedAmountModal
          isOpen={showEditApprovedAmountModal}
          onClose={() => setShowEditApprovedAmountModal(false)}
          onUpdate={async (amount, reason) => {
            const result = await updateApprovedAmount(selectedRequest.id, amount, reason);
            if (result.success) setShowEditApprovedAmountModal(false);
          }}
          request={selectedRequest}
        />
      )}

      {showEditSalaryAdvanceTypeModal && (
        <EditSalaryAdvanceTypeModal
          isOpen={showEditSalaryAdvanceTypeModal}
          onClose={() => setShowEditSalaryAdvanceTypeModal(false)}
          onUpdate={async (type) => {
            const result = await updateSalaryAdvanceType(selectedRequest.id, type);
            if (result.success) setShowEditSalaryAdvanceTypeModal(false);
          }}
          request={selectedRequest}
        />
      )}

      {showEmployeeSARHistoryModal && (
        <ViewEmployeeSARHistoryModal
          isOpen={showEmployeeSARHistoryModal}
          onClose={() => setShowEmployeeSARHistoryDetailsModal(false)}
          employeeSARHistory={employeeSARHistory}
        />
      )}

      <ConfirmationModal
        visible={showConfirmApproved}
        onClose={() => handleCloseConfirm()}
        onConfirm={async () => {
          const result = await handleAction(selectedRequest.id, "approve");
          if (result.success) handleCloseConfirm();
        }}
        title={confirmationTitle}
        // MODIFIED: Specific confirmation message
        message={`Are you sure you want to approve the requested amount of ₱${selectedRequest?.amount?.toLocaleString()} for ${selectedRequest?.referenceNumber}?`}
        // ADDED: Note to prompt user about changing the amount
        noteMessage={isAmountBase ? "If the amount is incorrect, please cancel this and edit the request before approving." : ''}
        confirmText={isAmountBase ? "Yes, Approve Amount" : "Yes"}
        type="info"
      />

      <DisapproveReasonModal
        isOpen={showDisapprove}
        onSave={async (reason) => {
          const result = await handleAction(disapproveTarget, "disapprove", reason);
          if (result.success) setShowDisapprove(false);
        }}
        onClose={() => setShowDisapprove(false)}
      />


      <LoadingSpinner loading={loading || sarLoading} />

      <GeneralMessage
        form={alertProps.showMessage}
        message={alertProps.message}
        isSuccess={alertProps.isSuccess}
      />

    </div>
  );
}